import countryToCurrency from 'country-to-currency';
import { format } from 'date-fns';
import { PurchaseCartResponse } from 'services/cart';
import { LanguageField } from 'services/configuration';
import { PrintData } from 'services/hardware';
import { Kiosk } from 'services/kiosk';

/**
 * Formats title to have first character of each word to be capitalized
 * @param {string} title - title to format
 */
export const formatTitle = (title: string, locale?: string) => {
  if (locale === 'frCA' || locale === 'frFR') {
    return title;
  }
  return title
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

export const formatHardwareTitle = (title: string) => {
  return title
    .split(/(?=[A-Z])/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

/**
 * Formats the number into currency
 * @param {number} number - number to format
 * @param {object} config - configs to put into numberformat fn
 * @param {LanguageField[]} languageSettings - default locale will be used
 */
export const formatCurrency = (number: number, config?: { [x: string]: any }, languageSettings?: LanguageField[]) => {
  const defaultLanguage = languageSettings && languageSettings.find((item) => item.isDefault);
  const defaultLanguageCode = defaultLanguage && defaultLanguage.langCode;
  const localeStr =
    defaultLanguageCode && defaultLanguageCode.length > 2
      ? `${defaultLanguageCode.substring(0, 2)}-${defaultLanguageCode.substring(2)}`
      : 'en-US';

  const numberFormat = new Intl.NumberFormat(localeStr, {
    style: 'currency',
    currency: getCurrency(localeStr),
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...config
  });

  return numberFormat.format(number);
};

/**
 * Formats the string to "--" if string is null
 * @param {string} str - string to format
 */
export const orDash = (str: string | null) => {
  if (str && str.length) return str;
  return '--';
};

/**
 * Formats the string into -- # noun (e.g. 5 tickets)
 * @param {number} count - number to input
 * @param {string} noun - word to pluralize
 * @param {suffix} suffix - string to form derivative
 */
export const pluralize = (count: number, noun: string, suffix: string = 's') => {
  return count === 1 ? `${count} ${noun}` : `${count} ${noun}${suffix}`;
};

export const replaceAll = (str: string, find: string, replace: string): string => {
  return str.replace(new RegExp(find, 'g'), replace);
};

export const formatText = (msg: string, flowTemplate?: string) => {
  if (flowTemplate === 'fair') {
    return msg.toUpperCase();
  }
  return msg;
};

const getCurrency = (locale: string) => {
  const countryCode: string = locale.slice(-2);
  const countryCurrencyCodes: any = countryToCurrency;
  const currencyCode = countryCurrencyCodes[countryCode];
  return currencyCode;
};

/**
 * Replace supported placeholders with data from the cart, kiosk or printData.
 * This is only used for printing a receipt in the fair flow.
 */
export const replacePlaceholdersInReceiptTemplate = (
  templateStr: string,
  cart: PurchaseCartResponse,
  kiosk: Kiosk,
  printData: PrintData
) => {
  const totalCount = printData.total;
  const printedCount = printData.printJobs.filter((job) => job.printed).length;

  let updatedStr = templateStr.replace('~MM/DD/YYYY~', format(new Date(), 'MM/dd/yyyy'));
  updatedStr = updatedStr.replace('~hh:mm:ss~', format(new Date(), 'hh:mm:ss'));
  updatedStr = updatedStr.replace('~CC~', (cart.transactionAmount && cart.transactionAmount.toString()) || '');
  updatedStr = replaceAll(updatedStr, '~DA~', (cart.transactionAmount && cart.transactionAmount.toFixed(2)) || '');
  updatedStr = updatedStr.replace('~DC~', printedCount.toString());
  updatedStr = updatedStr.replace('~EC~', totalCount.toString());
  if (totalCount === printedCount) {
    const firstPart = updatedStr.split('t***')[0].slice(0, updatedStr.split('t***')[0].length - 1);
    const secondPart = updatedStr
      .split('t***')[1]
      .split('***')[1]
      .slice(16, updatedStr.split('t***')[1].length - 1);
    updatedStr = firstPart + secondPart;
    updatedStr = updatedStr.replace('\\u001b\\u0045', '');
  }
  updatedStr = updatedStr.replace('~CT~', cart.cardType || 'Card');
  updatedStr = updatedStr.replace(
    '~ZZZZ~',
    (cart.cardNumber && cart.cardNumber.slice(cart.cardNumber.length - 4)) || 'XXXX'
  );
  updatedStr = updatedStr.replace('~999999~', cart.orderId);
  updatedStr = updatedStr.replace('~NNN~', kiosk.name);

  return updatedStr;
};
